// Admin API client for the dashboard
import axios from "axios";
import { auth } from "../../../firebaseConfig";

declare const process: any;
const BASE = process.env.REACT_APP_API_BASE || "http://127.0.0.1:5000";

async function authHeader(): Promise<{ Authorization?: string }> {
	try {
		const user = auth.currentUser;
		if (!user) return {};
		const token = await user.getIdToken();
		return { Authorization: `Bearer ${token}` };
	} catch (e) {
		// silent fail — caller can handle empty header
		return {};
	}
}

const api = {
	async getFlightOrders(page = 1, per_page = 20) {
		// admin_flight blueprint is registered under /admin_flight
		const headers = await authHeader();
		return axios
			.get(`${BASE}/admin_flight/flight-orders?page=${page}&per_page=${per_page}`, { headers })
			.then((r) => r.data);
	},

	async getHotelOrders(page = 1, per_page = 20) {
		// admin_hotel blueprint is registered under /admin_hotel
		const headers = await authHeader();
		return axios
			.get(`${BASE}/admin_hotel/hotel-orders?page=${page}&per_page=${per_page}`, { headers })
			.then((r) => r.data);
	},

	async getUsers(page = 1, per_page = 20) {
		// users list is under admin blueprint at /admin/users
		const headers = await authHeader();
		return axios.get(`${BASE}/admin/users?page=${page}&per_page=${per_page}`, { headers }).then((r) => r.data);
	},

	/**
	 * Combined invoices fetch: fetch flight and hotel orders and normalize to a single invoice list.
	 */
	async getInvoices(page = 1, per_page = 50) {
		const headers = await authHeader();

		const [flightResp, hotelResp] = await Promise.allSettled([
			axios.get(`${BASE}/admin_flight/flight-orders?page=${page}&per_page=${per_page}`, { headers }),
			axios.get(`${BASE}/admin_hotel/hotel-orders?page=${page}&per_page=${per_page}`, { headers }),
		]);

		const invoices: any[] = [];

		if (flightResp.status === "fulfilled" && Array.isArray(flightResp.value.data?.flight_orders)) {
			for (const f of flightResp.value.data.flight_orders) {
				invoices.push({
					id: f.flight_order_id || f.flight_order_db_id || null,
					type: "flight",
					bookingId: f.order_id || f.id || null,
					pnr: f.pnr,
					amount: f.total_price ?? f.total ?? null,
					currency: f.currency || "",
					createdAt: f.departure_time || f.created_at || null,
					raw: f,
				});
			}
		}

		if (hotelResp.status === "fulfilled" && Array.isArray(hotelResp.value.data?.hotel_orders)) {
			for (const h of hotelResp.value.data.hotel_orders) {
				invoices.push({
					id: h.hotel_order_id || null,
					type: "hotel",
					bookingId: h.order_id || null,
					ref: h.reference_number || null,
					amount: h.total_price ?? h.total_fare ?? null,
					currency: h.currency || "",
					createdAt: h.check_in_date || h.created_at || null,
					raw: h,
				});
			}
		}

		return { invoices, total: invoices.length };
	},

	// Server-side invoices endpoint (preferred) — returns payments joined with booking info
	async getInvoicesFromServer(page = 1, per_page = 50) {
		const headers = await authHeader();
		return axios.get(`${BASE}/admin/invoices?page=${page}&per_page=${per_page}`, { headers }).then(r => r.data);
	},

	async downloadInvoice(type: 'flight'|'hotel'|'payment', id: string|number) {
		const headers = await authHeader();
		// return the full URL which is protected by bearer token — easiest for browser to open
		return { url: `${BASE}/admin/invoices/download?type=${encodeURIComponent(String(type))}&id=${encodeURIComponent(String(id))}`, headers };
	},

	/**
	 * Alias for ticket download — backend uses same /admin/invoices/download endpoint
	 * for both invoices and tickets (flight tickets may be stored under tickets table)
	 */
	async downloadTicket(type: 'flight'|'hotel', id: string|number) {
		const headers = await authHeader();
		return { url: `${BASE}/admin/invoices/download?type=${encodeURIComponent(String(type))}&id=${encodeURIComponent(String(id))}`, headers };
	},

	async getFlightOrderDetail(id: number) {
		const headers = await authHeader();
		return axios.get(`${BASE}/admin_flight/flight-orders/${id}`, { headers }).then(r => r.data);
	},

	async getHotelOrderDetail(id: number) {
		const headers = await authHeader();
		return axios.get(`${BASE}/admin_hotel/hotel-orders/${id}`, { headers }).then(r => r.data);
	},
	// ---------- NEW: OFFERS ----------
	async getOffers(page = 1, per_page = 10) {
		const headers = await authHeader();
		return axios
			.get(`${BASE}/admin_offer/offers?page=${page}&per_page=${per_page}`, { headers })
			.then((r) => r.data);
	},

	async createOffer(formData: FormData) {
		const headers = await authHeader();
		// Do NOT set Content-Type – axios will set it automatically with boundary
		return axios.post(`${BASE}/admin_offer/offers`, formData, { headers }).then((r) => r.data);
	},

	async updateOffer(offerId: number, formData: FormData) {
		const headers = await authHeader();
		return axios.put(`${BASE}/admin_offer/offers/${offerId}`, formData, { headers }).then((r) => r.data);
	},

	async deleteOffer(offerId: number) {
		const headers = await authHeader();
		return axios.delete(`${BASE}/admin_offer/offers/${offerId}`, { headers }).then((r) => r.data);
	},
	// ---------- NEW: COUPONS ----------
	async getCoupons(page = 1, per_page = 10) {
		const headers = await authHeader();
		return axios
			.get(`${BASE}/admin_offer/coupons?page=${page}&per_page=${per_page}`, { headers })
			.then((r) => r.data);
	},

	async createCoupon(formData: FormData) {
		const headers = await authHeader();
		return axios.post(`${BASE}/admin_offer/coupons`, formData, { headers }).then((r) => r.data);
	},

	async updateCoupon(couponId: number, formData: FormData) {
		const headers = await authHeader();
		return axios.put(`${BASE}/admin_offer/coupons/${couponId}`, formData, { headers }).then((r) => r.data);
	},

	async deleteCoupon(couponId: number) {
		const headers = await authHeader();
		return axios.delete(`${BASE}/admin_offer/coupons/${couponId}`, { headers }).then((r) => r.data);
	},
	// In api.ts
	// NEW: Tally export for flight bookings
	async exportFlightTally(params: URLSearchParams) {
		const headers = await authHeader();
		return axios.get(`${BASE}/admin_flight/flight-orders/tally-export?${params.toString()}`, {
			headers,
			responseType: 'blob'
		});
	},
	// In api.ts
async exportInvoicesTally(params: URLSearchParams) {
  const headers = await authHeader();
  return axios.get(`${BASE}/admin/invoices/tally-export?${params.toString()}`, {
    headers,
    responseType: 'blob'
  });
},
exportHotelBookingsTally: async (params: URLSearchParams) => {
  const headers = await authHeader();
  return axios.get(`${BASE}/admin_hotel/hotel-bookings/tally-export`, { 
    params, 
    headers, 
    responseType: 'blob' 
  });
},
}

export default api;